/**
 * webhook.api.js — Dashboard webhook subscription management.
 *
 * All routes below are internal dashboard routes authenticated via JWT session cookie.
 * They live under /api/webhooks/* and are proxied by setupProxy.js in dev.
 *
 * External API routes (/api/v1/external/webhooks/*) are for customers using API keys
 * from outside the dashboard — they are untouched.
 */

import axiosConfig from '../config/axiosConfig'; // session-cookie instance

export const listSubscriptions = () =>
  axiosConfig.get('/webhooks/subscriptions');

// Uses the internal session-auth route so dashboard users don't need an API key to create.
export const createSubscription = (data) =>
  axiosConfig.post('/webhooks/subscriptions/create', data);

export const getSubscription = (id) =>
  axiosConfig.get(`/webhooks/subscriptions/${id}`);

export const updateSubscription = (id, fields) =>
  axiosConfig.patch(`/webhooks/subscriptions/${id}`, fields);

export const deleteSubscription = (id) =>
  axiosConfig.delete(`/webhooks/subscriptions/${id}`);

export const testSubscription = (id) =>
  axiosConfig.post(`/webhooks/subscriptions/${id}/test`);

export const listDeliveries = (subscriptionId, filters = {}, page = 1, limit = 50) => {
  const params = { page, limit };
  if (subscriptionId) params.subscription_id = subscriptionId;
  if (filters.status) params.status = filters.status;
  return axiosConfig.get('/webhooks/deliveries', { params });
};

export const getDelivery = (id) =>
  axiosConfig.get(`/webhooks/deliveries/${id}`);

export const redriveDelivery = (deliveryId) =>
  axiosConfig.post(`/webhooks/deliveries/${deliveryId}/redrive`);

export const redriveAllDead = async (subscriptionId) => {
  const res = await listDeliveries(subscriptionId, { status: 'dead' }, 1, 200);
  const body = res.data;
  const items =
    body?.data?.items || body?.entity?.items || body?.items || [];
  let redriven = 0;
  for (const item of items) {
    try { await redriveDelivery(item.id); redriven++; } catch { /* skip */ }
  }
  return { data: { redriven } };
};

const webhookApi = {
  listSubscriptions,
  createSubscription,
  getSubscription,
  updateSubscription,
  deleteSubscription,
  testSubscription,
  listDeliveries,
  getDelivery,
  redriveDelivery,
  redriveAllDead,
};

export default webhookApi;
